import { Component, signal, output, inject, OnInit, LOCALE_ID, DestroyRef, effect } from '@angular/core';
import { optionalName } from '../../../../../validators/name-rules';
import { Location, CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductsLookupService } from '../../../../../services/product/lookup.service';
import { ProductsFileMgt } from '../../../../../services/product/file-management/products.file-mgt.service';
import { ProductService } from '../../../../../services/product/product.service';
import { ProductImageItem, BACKEND_ERROR_FIELD_MAP, LookupItem } from './product-info-step.model';
import { HasSubProductsToggle } from './has-sub-products-toggle/has-sub-products-toggle';
import { ProductBasicInfo } from './product-basic-info/product-basic-info';
import { ProductImages } from './product-images/product-images';
import { ProductPrices } from './product-prices/product-prices';
import { CancelDialog } from './cancel-dialog/cancel-dialog';
import { CreateProductDTO } from '../../../../../pages/admin-dashboard/products/product.model';
import {
  arabicCharactersValidator,
  englishCharactersValidator,
} from '../../../../admin-dashboard-component/products/edit-product-form/utils/validation';
import { noOnlySpacesValidator } from '../../../../../validators/no-whitespace.validator';
import { LookupVTO } from '../../../../../pages/admin-dashboard/admin-companies/admin-companies.model';

@Component({
  selector: 'app-product-info-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HasSubProductsToggle,
    ProductBasicInfo,
    ProductImages,
    ProductPrices,
    CancelDialog,
  ],
  templateUrl: './product-info-step.html',
  styleUrl: './product-info-step.scss',
})
export class ProductInfoStep implements OnInit {
  private fb = inject(FormBuilder);
  private lookupService = inject(ProductsLookupService);
  private fileMgt = inject(ProductsFileMgt);
  private productService = inject(ProductService);
  private localeId = inject(LOCALE_ID);
  private location = inject(Location);
  private destroyRef = inject(DestroyRef);

  onComplete = output<{ id: number; hasSub: boolean; titleEn: string; titleAr: string }>();
  onToggleSub = output<boolean>();

  loading = signal(false);
  imageErrors = signal<string[]>([]);
  categories = signal<LookupItem[]>([]);
  mfgCompanies = signal<LookupItem[]>([]);
  subCategories = signal<LookupItem[]>([]);
  zones = signal<LookupVTO[]>([]);
  selectedImages = signal<ProductImageItem[]>([]);
  showCancelDialog = signal(false);
  hasSubProducts = signal(false);

  productForm = this.fb.group({
    englishName: [
      '',
      [Validators.maxLength(255), englishCharactersValidator(), noOnlySpacesValidator()],
    ],
    arabicName: ['', [Validators.required, Validators.maxLength(255), arabicCharactersValidator(), noOnlySpacesValidator()]],
    descriptionEn: ['', [Validators.maxLength(2000), englishCharactersValidator()]],
    descriptionAr: ['', [Validators.maxLength(2000), arabicCharactersValidator()]],
    category: [''],
    subCategory: [''],
    mfgCompany: ['', Validators.required],
    isRefrigerated: [false],
    zone: ['', Validators.required],
    peoplePrice: [
      null as number | null,
      [Validators.min(0.01), Validators.max(1000000), Validators.pattern(/^\d+(\.\d{1,2})?$/)],
    ],
    sellerDiscount: [
      null as number | null,
      [Validators.min(0), Validators.max(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)],
    ],
    sellerPrice: [{ value: '', disabled: true }],
    pharmacyDiscount: [
      null as number | null,
      [Validators.min(0), Validators.max(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)],
    ],
    pharmacyPrice: [{ value: '', disabled: true }],
    barcode: ['', [Validators.minLength(13), Validators.maxLength(13), Validators.pattern(/^\d{13}$/)]],
  });

  constructor() {
    effect(() => {
      const zoneCtrl = this.productForm.get('zone');
      if (this.hasSubProducts()) {
        zoneCtrl?.clearValidators();
      } else {
        zoneCtrl?.setValidators(Validators.required);
      }
      zoneCtrl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnInit() {
    this.loadLookups();

    this.productForm
      .get('category')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => val && this.loadSubCategories(Number(val)));

    this.productForm
      .get('peoplePrice')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calcSellerPrice();
        this.calcPharmacyPrice();
      });

    this.productForm
      .get('sellerDiscount')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.calcSellerPrice());

    this.productForm
      .get('pharmacyDiscount')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.calcPharmacyPrice());
  }

  private calcSellerPrice(): void {
    const people = this.productForm.get('peoplePrice')!.value;
    const discount = this.productForm.get('sellerDiscount')!.value;
    const result =
      people != null && people > 0 && discount != null
        ? (people - (people * discount) / 100).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '';
    this.productForm.get('sellerPrice')!.setValue(result);
  }

  private calcPharmacyPrice(): void {
    const people = this.productForm.get('peoplePrice')!.value;
    const discount = this.productForm.get('pharmacyDiscount')!.value;
    const result =
      people != null && people > 0 && discount != null
        ? (people - (people * discount) / 100).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '';
    this.productForm.get('pharmacyPrice')!.setValue(result);
  }

  onToggle(val: boolean): void {
    this.hasSubProducts.set(val);
    this.onToggleSub.emit(val);
  }

  loadLookups(): void {
    forkJoin({
      categories: this.lookupService.getLookupCategories(),
      companies: this.lookupService.getLookupMFGCompanies(),
      zones: this.lookupService.getLookupZones(),
    }).subscribe((res: any) => {
      this.categories.set(res.categories.list);
      this.mfgCompanies.set(res.companies.list);
      this.zones.set(res.zones.list ?? []);
    });
  }

  loadSubCategories(id: number): void {
    this.lookupService.getLookupSubCategories(id).subscribe((res: any) => this.subCategories.set(res.list ?? []));
  }

  handleFileSelect(files: File[]): void {
    const errors: string[] = [];
    const remaining = 5 - this.selectedImages().length;

    if (files.length > remaining) {
      errors.push($localize`:@@product.validation.imageMax:Maximum 5 images allowed`);
    }

    const valid = files.slice(0, remaining).filter((file) => {
      if (file.size > 5 * 1024 * 1024) {
        errors.push(`${file.name}: ` + $localize`:@@product.validation.imageSize:File size exceeds 5MB limit`);
        return false;
      }
      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
        errors.push(
          `${file.name}: ` +
            $localize`:@@product.validation.imageFormat:Invalid file format. Please upload JPG, PNG, or WebP`,
        );
        return false;
      }
      return true;
    });

    this.imageErrors.set(errors);

    valid.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e: any) =>
        this.selectedImages.update((prev) => [
          ...prev,
          { file, preview: e.target.result, isPrimary: prev.length === 0 },
        ]);
      reader.readAsDataURL(file);
    });
  }

  setPrimary(index: number): void {
    this.selectedImages.update((imgs) => imgs.map((img, i) => ({ ...img, isPrimary: i === index })));
  }

  removeImg(index: number): void {
    this.selectedImages.update((imgs) => {
      const updated = imgs.filter((_, i) => i !== index);
      if (imgs[index].isPrimary && updated.length > 0) {
        updated[0].isPrimary = true;
      }
      return updated;
    });
    this.imageErrors.set([]);
  }

  confirmCancel(): void {
    this.showCancelDialog.set(false);
    this.location.back();
  }

  private applyBackendError(code: string, messageAr?: string): void {
    const field = BACKEND_ERROR_FIELD_MAP[code];
    if (!field) return;

    let msg: string;
    if (this.localeId.startsWith('ar') && messageAr) {
      msg = messageAr;
    } else if (code === 'PRODUCT-0003') {
      msg = $localize`:@@product.validation.titleEnExists:An item with this English title already exists.`;
    } else if (code === 'PRODUCT-0004') {
      msg = $localize`:@@product.validation.titleArExists:An item with this Arabic title already exists.`;
    } else if (code === 'PRODUCT-0002') {
      msg = $localize`:@@product.validation.barcodeUnique:Barcode must be unique. This barcode already exists in the system.`;
    } else {
      return;
    }

    this.productForm.get(field)!.setErrors({ backendError: msg });
    this.productForm.get(field)!.markAsTouched();
  }

  submit(): void {
    const hasSub = this.hasSubProducts();

    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }

    if (this.selectedImages().length === 0) {
      this.imageErrors.set([$localize`:@@product.validation.imageRequired:At least one product image is required`]);
      return;
    }

    this.loading.set(true);

    const uploadTasks = this.selectedImages().map((img) => this.fileMgt.uploadImage(img.file));

    forkJoin(uploadTasks).subscribe({
      next: (results: any[]) => {
        const fids = results.map((r: any) => r.fid);
        const vals = this.productForm.getRawValue();
        const primaryIdx = this.selectedImages().findIndex((i) => i.isPrimary);
        const selectedProfileFid = fids[primaryIdx >= 0 ? primaryIdx : 0] ?? '';

        const payload: CreateProductDTO = {
          titleEn: optionalName(vals.englishName),
          titleAr: vals.arabicName!.trim(),
          descriptionEn: vals.descriptionEn || undefined,
          descriptionAr: vals.descriptionAr || undefined,
          mfgCompanyId: Number(vals.mfgCompany),
          hasSubProducts: hasSub,
          isRefrigerated: vals.isRefrigerated ?? false,
          categoryId: vals.category ? Number(vals.category) : undefined,
          subCategoryId: vals.subCategory ? Number(vals.subCategory) : undefined,
          barcode: vals.barcode || undefined,
          imagesFids: fids,
          profileImageFid: selectedProfileFid,
          ...(!hasSub && {
            zoneId: vals.zone ? Number(vals.zone) : undefined,
            customerPrice: vals.peoplePrice ? Number(vals.peoplePrice) : undefined,
            sellerPriceDiscountPercentage: vals.sellerDiscount != null ? Number(vals.sellerDiscount) : undefined,
            businessPriceDiscountPercentage: vals.pharmacyDiscount != null ? Number(vals.pharmacyDiscount) : undefined,
          }),
        };
        this.productService.createProduct(payload).subscribe({
          next: (res: any) => {
            this.loading.set(false);
            this.onComplete.emit({
              id: res.id,
              hasSub,
              titleEn: vals.englishName || '',
              titleAr: vals.arabicName!,
            });
          },
          error: (err: any) => {
            this.loading.set(false);
            const code = err?.error?.code;
            if (code && BACKEND_ERROR_FIELD_MAP[code]) {
              this.applyBackendError(code, err?.error?.messageAr);
            }
          },
        });
      },
      error: () => this.loading.set(false),
    });
  }
}
